import Block from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
  ColDef,
  GetRowIdParams,
  ICellRendererParams,
  IRowNode,
  ITooltipParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { AgTable } from '../../../components/AgTable/AgTable';
import { FadeInGridWrapper } from '../../../components/grid-renderers/FadeInGridWrapper';
import { ROUTES } from '../../../constants/RouteNameMapping';
import { CompanyPeriodRequestStatus } from '../../../schemas/CompanyPeriodRequest.schema';
import { colors } from '../../../theme/colors';
import { AgFormatterService } from '../../../util/ag-formatter-service';
import { withOverrides } from '../../../util/ag-grid-utils';
import { StringFormatterId } from '../../../util/formatters/StringFormatters';
import { useSchemaToGrid } from '../../../util/schema-utils';
import { toURLFriendlyStringWithDashes } from '../../../util/url-utils';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { companyPeriodRequestGridSchema } from './company-period-request-grids.view-model';
import { CompanyPeriodRequestManageViewModel } from './company-period-request.view-model';
import { EmailStatusModal } from './EmailStatusModal';
import {
  buildRequestReminderMailtoHref,
  formatFyeTooltip,
  getDueDateDisplay,
  periodValueFormatter,
} from './manage-request-utils';

// Handlers threaded to cell renderers via AgTable's `context` prop (the
// renderers live outside the React tree).
export interface ManageGridContext {
  onShowEmailDetails?: (row: CompanyPeriodRequestManageViewModel) => void;
}

export type CompanyPeriodRequestManageGridProps = {
  requests: CompanyPeriodRequestManageViewModel[];
  selectedStatus: CompanyPeriodRequestStatus | string;
  mode?: 'v1' | 'v2';
};

const V1_COLUMN_IDS = ['companyId', 'period', 'status', 'actions'] as const;
const V2_COLUMN_IDS = [
  'companyId',
  'period',
  'templateName',
  'sentAt',
  'emailStatus',
  'status',
  'dueDate',
  'actions',
] as const;

export const defaultColDef: ColDef<CompanyPeriodRequestManageViewModel> = {
  flex: 1,
  resizable: true,
  sortable: true,
  filter: 'agSetColumnFilter',
  enableRowGroup: true,
  minWidth: 150,
};

const getRowId = (params: GetRowIdParams) => params.data?.id;

export function CompanyPeriodRequestManageGrid(props: CompanyPeriodRequestManageGridProps) {
  const { requests, selectedStatus, mode = 'v1' } = props;
  const isV2 = mode === 'v2';
  const { showEmailStatuses } = useFlags<MaggieFeatureFlags>();
  const schemaToGrid = useSchemaToGrid();
  const [emailDetailsRow, setEmailDetailsRow] = useState<CompanyPeriodRequestManageViewModel | null>(null);

  // Only expose the email-details handler (and thus the row button) when the
  // flag is on; the modal and the emailStatus column are gated the same way.
  const gridContext = useMemo<ManageGridContext>(
    () => ({ onShowEmailDetails: showEmailStatuses ? setEmailDetailsRow : undefined }),
    [showEmailStatuses]
  );

  const colDefs = useMemo(() => {
    const capitalized = AgFormatterService.get().getFormatterForId(StringFormatterId.capitalizedString);
    const columnIds = (isV2 ? [...V2_COLUMN_IDS] : [...V1_COLUMN_IDS]).filter(
      (id) => id !== 'emailStatus' || showEmailStatuses
    );

    return withOverrides(schemaToGrid(companyPeriodRequestGridSchema(), columnIds), {
      companyId: {
        cellRendererParams: {
          displayLogoInGroupRow: true,
        },
        initialSort: 'asc',
        // V1 groups rows by company; V2 shows companies as flat rows (no grouping).
        ...(isV2 ? {} : { rowGroup: true, initialHide: true }),
      },
      actions: {
        cellRenderer: CompanyPeriodRequestManageActionCellRenderer,
        pinned: 'right',
        // V2 has three actions (email + email-details + financials); V1 has one.
        width: isV2 ? 160 : 80,
        maxWidth: isV2 ? 160 : 80,
        resizable: false,
        sortable: false,
        filter: false,
        suppressHeaderMenuButton: true,
        suppressMovable: true,
      },
      status: {
        valueFormatter: capitalized,
        filterParams: { valueFormatter: capitalized },
        // V2 filters status via the toggle tabs, so hide the grid column filter.
        ...(isV2 ? { filter: false } : {}),
      },
      period: {
        valueFormatter: periodValueFormatter,
        filterParams: { valueFormatter: periodValueFormatter },
        // The dropdown buckets periods on the firm (FYE-12) calendar, but this
        // column displays each row on its company's fiscal calendar — surface
        // that FYE on hover.
        ...(isV2
          ? {
              tooltipValueGetter: (params: ITooltipParams<CompanyPeriodRequestManageViewModel>) =>
                params.data ? formatFyeTooltip(params.data.fye) : undefined,
            }
          : {}),
      },
      ...(isV2
        ? {
            emailStatus: {
              cellRenderer: EmailStatusCellRenderer,
              filter: 'agSetColumnFilter',
              filterValueGetter: (p: ValueGetterParams<CompanyPeriodRequestManageViewModel>) =>
                emailStatusBucket(p.data),
              filterParams: {
                comparator: (a: string, b: string) =>
                  EMAIL_STATUS_BUCKETS.indexOf(a) - EMAIL_STATUS_BUCKETS.indexOf(b),
              },
            },
          }
        : {}),
      // dueDate is V2-only; guard the override so it can't leak V2 rendering into
      // V1 if the column is ever added there (consistent with status/period above).
      ...(isV2
        ? {
            dueDate: {
              cellRenderer: DueDateCellRenderer,
              // The schema gives date columns a date filter + date cellDataType. Disable
              // cellDataType and use a standard set filter whose values come from a
              // derived urgency bucket (Overdue / Due soon / Other) via filterValueGetter
              // (keyCreator is skipped by the set filter for primitive string values).
              filter: 'agSetColumnFilter',
              cellDataType: false,
              filterValueGetter: (p: ValueGetterParams<CompanyPeriodRequestManageViewModel>) =>
                dueDateBucket(p.data),
              filterParams: {
                comparator: (a: string, b: string) =>
                  DUE_DATE_BUCKETS.indexOf(a) - DUE_DATE_BUCKETS.indexOf(b),
              },
            },
          }
        : {}),
    });
  }, [schemaToGrid, isV2, showEmailStatuses]);

  const gridDefaultColDef = useMemo<ColDef<CompanyPeriodRequestManageViewModel>>(
    // V2 is flat: disable drag-to-group so no grouping can be re-introduced via the UI.
    () => (isV2 ? { ...defaultColDef, enableRowGroup: false } : defaultColDef),
    [isV2]
  );

  const isExternalFilterPresent = useCallback(() => {
    return selectedStatus !== 'All';
  }, [selectedStatus]);

  const doesExternalFilterPass = useCallback(
    (node: IRowNode<CompanyPeriodRequestManageViewModel>) => {
      return node.data?.status === selectedStatus;
    },
    [selectedStatus]
  );

  const autoGroupColumnDef: ColDef<CompanyPeriodRequestManageViewModel> = useMemo(
    () => ({
      filter: 'agGroupColumnFilter',
    }),
    []
  );

  return (
    <Stack height={'100%'}>
      <FadeInGridWrapper>
        <AgTable
          rowData={requests}
          columnDefs={colDefs}
          context={gridContext}
          tooltipShowDelay={isV2 ? 1000 : undefined}
          defaultColDef={gridDefaultColDef}
          autoGroupColumnDef={isV2 ? undefined : autoGroupColumnDef}
          rowGroupPanelShow={isV2 ? 'never' : 'always'}
          groupDefaultExpanded={-1}
          suppressMovableColumns={true}
          suppressRowDrag={true}
          // V2 has no totals/status bar; the menu bar's "Showing N requests" covers it.
          statusBar={
            isV2
              ? undefined
              : {
                  statusPanels: [
                    { statusPanel: 'agTotalRowCountComponent' },
                    { statusPanel: 'agFilteredRowCountComponent' },
                  ],
                }
          }
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          getRowId={getRowId}
        />
      </FadeInGridWrapper>
      {isV2 && showEmailStatuses && (
        <EmailStatusModal
          open={emailDetailsRow != null}
          request={emailDetailsRow}
          onClose={() => setEmailDetailsRow(null)}
        />
      )}
    </Stack>
  );
}

const DueDateSeverityColor = {
  'due-soon': colors.warning[60],
  overdue: colors.critical[60],
} as const;

export function DueDateCellRenderer(params: ICellRendererParams<CompanyPeriodRequestManageViewModel>) {
  const { data, value, valueFormatted } = params;
  if (!data) {
    return null;
  }

  const display = getDueDateDisplay(data);
  if (!display) {
    return <>{valueFormatted ?? value}</>;
  }

  // No label (e.g. overdue > 2 days) → show the plain date, still in the severity colour.
  return (
    <span style={{ color: DueDateSeverityColor[display.severity] }}>
      {display.label ?? valueFormatted ?? value}
    </span>
  );
}

export const DUE_DATE_BUCKETS: string[] = ['Overdue', 'Due soon', 'Other'];

/**
 * Buckets a row for the Due Date set filter, reusing `getDueDateDisplay` so the
 * filter categories match the cell colours. Everything that isn't due-soon /
 * overdue (future, completed, no due date) falls into "Other".
 */
export function dueDateBucket(data: CompanyPeriodRequestManageViewModel | undefined): string {
  const severity = data ? getDueDateDisplay(data)?.severity : undefined;
  if (severity === 'due-soon') {
    return 'Due soon';
  }
  if (severity === 'overdue') {
    return 'Overdue';
  }
  return 'Other';
}

export const EMAIL_STATUS_BUCKETS: string[] = ['Failed', 'Partial', 'Sent', '—'];

/** Buckets a row for the Email column's set filter (labels match the cell). */
export function emailStatusBucket(data: CompanyPeriodRequestManageViewModel | undefined): string {
  switch (data?.emailStatus) {
    case 'failed':
      return 'Failed';
    case 'partial':
      return 'Partial';
    case 'sent':
      return 'Sent';
    default:
      return '—';
  }
}

// Aggregate indicator: sent → success, partial → warning, failed → error, and
// a dash when nothing was emailed (or SB predates the rollup).
export function EmailStatusCellRenderer(params: ICellRendererParams<CompanyPeriodRequestManageViewModel>) {
  const { data } = params;
  if (!data || data.emailStatus == null) {
    return <span style={{ color: colors.neutral[40] }}>—</span>;
  }

  const failed = data.emailFailedCount ?? 0;
  const total = data.emailTotalCount ?? 0;
  const tooltip = failed > 0 ? `${failed} of ${total} recipients failed` : undefined;

  const content =
    data.emailStatus === 'sent' ? (
      <>
        <CheckCircleIcon color='success' fontSize='small' />
        <Typography variant='body2' color='text.secondary' noWrap>
          Sent
        </Typography>
      </>
    ) : data.emailStatus === 'partial' ? (
      <>
        <WarningAmberIcon color='warning' fontSize='small' />
        <Typography variant='body2' color='text.secondary' noWrap>
          Partial
        </Typography>
      </>
    ) : (
      <>
        <Block color='error' fontSize='small' />
        <Typography variant='body2' color='text.secondary' noWrap>
          Failed
        </Typography>
      </>
    );

  const row = (
    <Stack direction='row' alignItems='center' gap='0.5rem' height='100%'>
      {content}
    </Stack>
  );

  return tooltip ? <Tooltip title={tooltip}>{row}</Tooltip> : row;
}

export function CompanyPeriodRequestManageActionCellRenderer(
  params: ICellRendererParams<CompanyPeriodRequestManageViewModel> & { context?: ManageGridContext }
) {
  const { data, node, context } = params;
  if (!data || node?.group) {
    return null;
  }
  const { companyId, companyName, recipients, requestType } = data;
  if (!companyId) {
    return null;
  }
  // Email-details action is V2-only (requestType present) and needs a handler.
  const showEmailDetails = requestType != null && Boolean(context?.onShowEmailDetails);

  // `recipients` is only present on V2 rows. Always reserve the email slot for
  // those rows (hidden when there's no one to email) so the financials icon
  // keeps the same position whether or not the email icon is shown.
  const emailApplicable = Array.isArray(recipients);
  const hasRecipients = emailApplicable && recipients.length > 0;
  const financialsPath = `/${ROUTES.COMPANY_PROFILES}/${toURLFriendlyStringWithDashes(companyName ?? '')}-${companyId}/${ROUTES.COMPANY_PROFILE_FINANCIALS}`;

  return (
    <Stack direction='row' alignItems='center' gap='.25rem'>
      {emailApplicable && (
        <IconButton
          component='a'
          href={hasRecipients ? buildRequestReminderMailtoHref(data) : undefined}
          target='_blank'
          rel='noopener noreferrer'
          color='secondary'
          size='medium'
          title={
            hasRecipients
              ? `Email recipients (${recipients?.join(', ')}) to complete this request`
              : undefined
          }
          sx={{ visibility: hasRecipients ? 'visible' : 'hidden' }}
        >
          <EmailOutlinedIcon sx={{ color: 'primary' }} />
        </IconButton>
      )}
      {showEmailDetails && (
        <IconButton
          color='secondary'
          size='medium'
          title='View email delivery details'
          onClick={() => context?.onShowEmailDetails?.(data)}
        >
          <FactCheckIcon sx={{ color: 'primary' }} />
        </IconButton>
      )}
      <IconButton
        component={Link}
        to={financialsPath}
        target='_blank'
        color='secondary'
        size='medium'
        title={`View ${companyName}'s financials`}
      >
        <ManageSearchIcon sx={{ color: 'primary' }} />
      </IconButton>
    </Stack>
  );
}
